import { useMemo, useState } from 'react';
import { Container } from './Container';
import { Button } from './Button';

function scrollToId(id) {
  const el = document.getElementById(id);
  if (!el) return;
  el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

export function Navbar({ path = '/' }) {
  const [open, setOpen] = useState(false);
  const isHome = path === '/';

  const items = useMemo(() => {
    if (!isHome) {
      return [
        { label: 'Home', href: '#/' },
        { label: 'Privacy Policy', href: '#/privacy' },
        { label: 'Terms of Service', href: '#/terms' },
      ];
    }

    return [
      { label: 'About', onClick: () => scrollToId('about') },
      { label: 'Services', onClick: () => scrollToId('services') },
      { label: 'Why Choose Us', onClick: () => scrollToId('why') },
      { label: 'Contact', onClick: () => scrollToId('contact') },
    ];
  }, [isHome]);

  return (
    <header className="sticky top-0 z-40 border-b border-slate-200/70 bg-white/80 backdrop-blur">
      <Container className="flex h-16 items-center justify-between">
        <a
          href="#/"
          className="flex items-center gap-2 text-sm font-semibold text-slate-900"
          aria-label="Grow With Call home"
        >
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-xl bg-sky-700 text-white">
            G
          </span>
          <span>Grow With Call</span>
        </a>

        <nav className="hidden items-center gap-1 md:flex" aria-label="Primary">
          {items.map((item) => (
            <NavItem
              key={item.label}
              item={item}
              onNavigate={() => setOpen(false)}
            />
          ))}
        </nav>

        <div className="hidden md:block">
          {isHome ? (
            <Button onClick={() => scrollToId('contact')}>Contact Us</Button>
          ) : (
            <Button as="a" href="#/" variant="secondary">
              Back to Home
            </Button>
          )}
        </div>

        <button
          type="button"
          className="inline-flex items-center justify-center rounded-xl p-2 text-slate-700 ring-1 ring-inset ring-slate-200 hover:bg-slate-50 md:hidden"
          aria-label={open ? 'Close menu' : 'Open menu'}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          <span className="text-base leading-none">{open ? '✕' : '☰'}</span>
        </button>
      </Container>

      {open && (
        <div className="border-t border-slate-200 bg-white md:hidden">
          <Container className="py-3">
            <nav className="flex flex-col gap-1" aria-label="Mobile">
              {items.map((item) => (
                <NavItem
                  key={item.label}
                  item={item}
                  mobile
                  onNavigate={() => setOpen(false)}
                />
              ))}
              {isHome && (
                <div className="pt-2">
                  <Button className="w-full" onClick={() => scrollToId('contact')}>
                    Contact Us
                  </Button>
                </div>
              )}
            </nav>
          </Container>
        </div>
      )}
    </header>
  );
}

function NavItem({ item, onNavigate, mobile = false }) {
  const className = mobile
    ? 'w-full rounded-xl px-3 py-2 text-left text-sm font-medium text-slate-800 hover:bg-slate-50'
    : 'rounded-xl px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 hover:text-slate-900';

  if (item.href) {
    return (
      <a className={className} href={item.href} onClick={onNavigate}>
        {item.label}
      </a>
    );
  }

  return (
    <button
      type="button"
      className={className}
      onClick={() => {
        item.onClick?.();
        onNavigate?.();
      }}
    >
      {item.label}
    </button>
  );
}

